import React from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { PaymentErrorModalContent } from '@/components/PaymentErrorModalContent';
import { Screen } from '@/components/ScreenComponent';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PaymentMethodTypes } from '@/feature/deposit/const';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { popToHome } from '@/navigation/navigation';
import { WithdrawStackParamList } from '@/navigation/types';
import { FailedWithdrawalNavigationProp } from '@/navigation/withdraw/types';

import { useTrustlyWithdrawalSalvage } from '../hooks/use-trustly-withdrawal-salvage';
import { useGoToWithdrawHome } from '../hooks/use-withdraw-navigation';

type NavigationFailedWithdrawalProps = NativeStackScreenProps<WithdrawStackParamList, 'FailedWithdrawal'>;

export const FailedWithdrawal = ({ route }: NavigationFailedWithdrawalProps) => {
    const { t } = useTranslation(['wallet', 'common']);
    const navigation = useNavigation<FailedWithdrawalNavigationProp>();
    const rootNavigation = useRootNavigation();

    usePreventAndroidBackNavigation();

    const { errorCode, selectedMethod } = route?.params ?? {};

    const attemptedOnlyNonTrustly = !!selectedMethod && selectedMethod.type !== PaymentMethodTypes.Trustly;
    const salvageActions = useTrustlyWithdrawalSalvage({ attemptedOnlyNonTrustly });
    const goToWithdrawHome = useGoToWithdrawHome();

    const secondaryButtonComponent = salvageActions.isApplicable ? (
        <Button label={t('common:return_to_withdrawals')} hierarchy={'subtle'} onPress={goToWithdrawHome} />
    ) : (
        <Button
            label={t('common:return_to_lobby')}
            hierarchy={'subtle'}
            onPress={() => {
                BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_FAIL_LOBBY);
                popToHome(rootNavigation);
            }}
        />
    );

    const handlePrimaryButtonPress = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_FAIL_RETRY);
        if (salvageActions.isApplicable) {
            salvageActions.onPress();
        } else {
            navigation.goBack();
        }
    };

    return (
        <Screen edges={['top', 'bottom']}>
            <PaymentErrorModalContent
                transactionType="withdrawal"
                onClose={() => popToHome(rootNavigation, 'account')}
                errorCode={errorCode}
                primaryButton={salvageActions.isApplicable ? salvageActions.label : t('common:retry')}
                showSecondaryButton
                secondaryButton={secondaryButtonComponent}
                handleSecondaryButtonPress={() => popToHome(rootNavigation)}
                showContactSupport
                handlePrimaryButtonPress={handlePrimaryButtonPress}
            />
        </Screen>
    );
};
